import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";

export const Route = createFileRoute("/_authenticated/checkout/return")({
  validateSearch: (search: Record<string, unknown>) => ({
    session_id: typeof search.session_id === "string" ? search.session_id : undefined,
  }),
  component: CheckoutReturn,
});

type Status = "checking" | "success" | "delayed";

function CheckoutReturn() {
  const { session_id } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status>(session_id ? "checking" : "delayed");

  useEffect(() => {
    if (!session_id) return;
    let cancelled = false;
    const startTime = Date.now();
    const env = (() => {
      try {
        return getStripeEnvironment();
      } catch {
        return "sandbox" as const;
      }
    })();

    const poll = async () => {
      if (cancelled) return;
      const { data: session } = await supabase.auth.getSession();
      const userId = session.session?.user.id;
      if (!userId) {
        if (Date.now() - startTime < 30_000) setTimeout(poll, 2000);
        else setStatus("delayed");
        return;
      }

      const { data: sub } = await supabase
        .from("subscriptions")
        .select("status, current_period_end")
        .eq("user_id", userId)
        .eq("environment", env)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      const active =
        sub &&
        ["active", "trialing", "past_due"].includes(sub.status) &&
        (!sub.current_period_end || new Date(sub.current_period_end) > new Date());

      if (active) {
        if (cancelled) return;
        setStatus("success");
        qc.invalidateQueries({ queryKey: ["my-profile"] });
        qc.invalidateQueries({ queryKey: ["subscription"] });

        const fire = (particleRatio: number, opts: confetti.Options) =>
          confetti({
            origin: { y: 0.7 },
            ...opts,
            particleCount: Math.floor(200 * particleRatio),
          });
        fire(0.25, { spread: 26, startVelocity: 55 });
        fire(0.2, { spread: 60 });
        fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
        fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
        fire(0.1, { spread: 120, startVelocity: 45 });

        toast.success("Subscription active — welcome to Pro 🚀", { duration: 5000 });
        setTimeout(() => {
          if (!cancelled) navigate({ to: "/dashboard" });
        }, 2500);
        return;
      }

      if (Date.now() - startTime < 30_000) {
        setTimeout(poll, 2000);
      } else {
        setStatus("delayed");
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [session_id, qc, navigate]);

  return (
    <main className="flex flex-1 items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-primary/40 bg-gradient-to-b from-primary/10 to-card/40 p-8 text-center">
        {status === "checking" && (
          <>
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/20 text-primary">
              <Loader2 className="h-7 w-7 animate-spin" />
            </div>
            <h1 className="font-display text-2xl font-bold">Activating your account…</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Payment confirmed. We're unlocking your plan — this usually takes just a few seconds.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/20 text-primary">
              <Check className="h-7 w-7" />
            </div>
            <h1 className="font-display text-2xl font-bold">Welcome to Pro 🚀</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              All pairs unlocked. Redirecting you to the dashboard…
            </p>
            <div className="mt-6">
              <Button onClick={() => navigate({ to: "/dashboard" })} size="lg">
                Go to dashboard
              </Button>
            </div>
          </>
        )}

        {status === "delayed" && (
          <>
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/20 text-amber-500">
              <Mail className="h-7 w-7" />
            </div>
            <h1 className="font-display text-2xl font-bold">Payment received</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {session_id
                ? "Your account is activating shortly. If your plan hasn't updated within 5 minutes, please email billing@forexmind.ai with your session ID."
                : "No session information found."}
            </p>
            {session_id && (
              <p className="mt-2 break-all rounded-md bg-card/60 px-2 py-1 text-xs text-muted-foreground">
                {session_id}
              </p>
            )}
            <div className="mt-6 flex flex-col gap-2">
              <Button onClick={() => navigate({ to: "/dashboard" })} size="lg">
                Continue to dashboard
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/profile" })}>
                Manage billing
              </Button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
